import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fetchProducts } from "@/lib/products";
import { waLink, formatPrice, scrollToId } from "@/lib/store";
import { track } from "@/lib/analytics";
import { estimateTrade, hasBattery, tradeModels, replacedParts, TRADE_PARTS } from "@/lib/tradeCatalog";
import { fetchOrderPrices } from "@/lib/orderPrices";
import { ORDER_OPTION, wantedInfo } from "./TradeWanted";
import { WhatsAppIcon } from "../WhatsAppIcon";
import { TradeDevice } from "./TradeDevice";
import { TradeCondition } from "./TradeCondition";
import { TradePrints } from "./TradePrints";
import { TradeEstimate } from "./TradeEstimate";
import { TradeStickyBar } from "./TradeStickyBar";
import { diffPayText } from "./TradeInstallments";
import { Question, tradeInput } from "./TradeParts";

export const MAX_DEVICES = 5;
const newDevice = (category = "iphone") => ({ uid: Math.random().toString(36).slice(2), category, model: "", storage: "", color: "", battery: "", has_box: null, parts_replaced: null, parts: [], parts_detail: "", unknown_part: false, bought_at_rc: false, marks: null, screen_cracked: null, back_cracked: null, prints: {} });
const newWant = () => ({ wanted_product_id: "", wanted_order: { model: "", storage: "", condition: "novo" } });
const PART = Object.fromEntries(TRADE_PARTS);
const yes = (b) => (b ? "Sim" : "Não");
const MARK = { nenhuma: "Nenhuma", leves: "Leves", moderadas: "Moderadas", severas: "Severas" };
const sfxOf = (i) => (i === 0 ? "" : `-${i + 1}`);
const updateAt = (setList, i) => (updater) => setList((list) => list.map((x, j) => (j === i ? (typeof updater === "function" ? updater(x) : updater) : x)));

// Primeira pendência de um aparelho: [mensagem, id do campo para levar o cliente até ele].
const missingDevice = (d, i, many) => {
  const sfx = sfxOf(i);
  const who = many ? ` (aparelho ${i + 1})` : "";
  const needsGb = tradeModels(d.category).find((m) => m.name === d.model)?.storages?.length > 0;
  if (!d.model) return [`Escolha o modelo do seu aparelho${who}.`, `trade-q-model${sfx}`];
  if (needsGb && !d.storage) return [`Escolha o armazenamento (GB)${who}.`, `trade-q-model${sfx}`];
  if (hasBattery(d.category) && d.battery === "") return [`Informe a saúde da bateria${who}.`, `trade-q-battery${sfx}`];
  const open = [["has_box", "box"], ["parts_replaced", "parts"], ["marks", "marks"], ["screen_cracked", "screen"], ["back_cracked", "back"]].find(([k]) => d[k] === null);
  if (open) return [`Responda todas as perguntas sobre o estado do aparelho${who}.`, `trade-q-${open[1]}${sfx}`];
  return null;
};

const deviceLines = (d, est, n) => [
  n ? `*— Aparelho ${n} —*` : null,
  `*Aparelho:* ${[d.model, d.storage, d.color].filter(Boolean).join(" · ")}`,
  hasBattery(d.category) ? `*Saúde da bateria:* ${d.battery}%` : null,
  `*Possui caixa:* ${yes(d.has_box)}`,
  `*Peças trocadas:* ${d.parts_replaced ? replacedParts(d).map((p) => PART[p]).join(", ") : "Não"}${d.parts_replaced && d.parts_detail ? ` (${d.parts_detail})` : ""}`,
  `*Aparece "Peça desconhecida":* ${yes(d.unknown_part)}`,
  `*Comprado na RC iPhones:* ${yes(d.bought_at_rc)}`,
  `*Marcas de uso:* ${MARK[d.marks]}`,
  `*Tela trincada:* ${yes(d.screen_cracked)} · *Tampa trincada:* ${yes(d.back_cracked)}`,
  est ? `*Avaliação estimada no site:* ${formatPrice(est.value)}${est.bonus > 0 ? ` (inclui bônus de ${formatPrice(est.bonus)})` : ""} (sujeita à conferência)` : "*Avaliação:* aguardo a avaliação da loja",
];

const message = (name, devices, res, diffPay) => {
  const many = devices.length > 1;
  const ests = [res?.estimate, ...(res?.extra_estimates || [])];
  const wanted = [res?.wanted, ...(res?.extra_wanted || [])].filter(Boolean);
  const diff = res?.total?.difference;
  const change = res?.total?.change;
  const diffLine = change > 0 ? ` · volta estimada para mim: ${formatPrice(change)}` : diff != null ? ` · diferença estimada ${diff > 0 ? formatPrice(diff) : "R$ 0,00"}` : "";
  const wantedLine = wanted.map((w) => `${w.label}${w.source === "pedido" && w.price > 0 ? `, preço sugerido em média ${formatPrice(w.price)}` : ""}`).join(" + ");
  return [
    many ? `Olá, RC iPhones! Quero avaliar ${devices.length} aparelhos na troca.` : "Olá, RC iPhones! Quero avaliar meu aparelho na troca.",
    `*Nome:* ${name.trim()}`,
    ...devices.flatMap((d, i) => deviceLines(d, ests[i], many ? i + 1 : 0)),
    many && res?.total ? `*Total das avaliações:* ${formatPrice(res.total.value)}${res.total.all_priced ? "" : " + aparelhos para a loja avaliar"}` : null,
    wantedLine ? `*Quero levar:* ${wantedLine}${diffLine}` : null,
    diffPayText(diff, diffPay) ? `*Pagamento da diferença:* ${diffPayText(diff, diffPay)}` : null,
    `Enviei os prints pelo site (avaliação nº ${res?.code}).`,
  ].filter(Boolean).join("\n");
};

const devicePayload = (d) => {
  const { prints, uid: _uid, ...rest } = d;
  return { ...rest, battery: d.battery === "" ? null : Number(d.battery), parts: d.parts_replaced ? d.parts : [],
    images: Object.entries(prints || {}).filter(([, v]) => v).map(([label, data_url]) => ({ label, data_url })) };
};
const wantPayload = (w) => {
  const byOrder = w.wanted_product_id === ORDER_OPTION;
  return { product_id: byOrder ? null : w.wanted_product_id || null, order: byOrder && w.wanted_order.model && w.wanted_order.storage ? w.wanted_order : null };
};

export const TradeForm = () => {
  const [name, setName] = useState("");
  const [diffPay, setDiffPay] = useState(12);
  const [devices, setDevices] = useState([newDevice()]);
  const [wants, setWants] = useState([newWant()]);
  const [prices, setPrices] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderPrices, setOrderPrices] = useState([]);
  const [bonus, setBonus] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const mainButton = useRef(null);

  useEffect(() => {
    api.get("/trade-prices").then((r) => setPrices(Array.isArray(r?.data) ? r.data : [])).catch(() => setPrices([]));
    fetchProducts().then((list) => setProducts(Array.isArray(list) ? list : [])).catch(() => setProducts([]));
    fetchOrderPrices().then((list) => setOrderPrices(Array.isArray(list) ? list : [])).catch(() => setOrderPrices([]));
    api.get("/trade-bonus").then((r) => setBonus(Number(r?.data?.amount) || 0)).catch(() => setBonus(0));
  }, []);

  const items = useMemo(() => devices.map((d) => {
    const doc = prices.find((p) => p.model === d.model);
    return { device: d, priced: !!doc, est: estimateTrade(doc, d, bonus) };
  }), [devices, prices, bonus]);
  const allPriced = items.every((x) => x.est);
  const total = items.reduce((s, x) => s + (x.est?.value || 0), 0);
  const category = devices[0].category;
  const wantInfos = wants.map((w) => wantedInfo({ ...w, category }, products, orderPrices));
  const chosen = wantInfos.filter(Boolean);
  const wantedTotal = chosen.reduce((s, w) => s + w.price, 0);
  const diff = chosen.length && allPriced && chosen.every((w) => w.price > 0) ? wantedTotal - total : null;

  const addDevice = () => {
    setDevices((l) => [...l, newDevice(l[l.length - 1].category)]);
    setTimeout(() => scrollToId(`trade-device-${devices.length + 1}`), 60);
  };
  const removeDevice = (i) => setDevices((l) => l.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    const why = devices.map((d, i) => missingDevice(d, i, devices.length > 1)).find(Boolean) || (name.trim().length < 2 ? ["Digite seu nome.", "trade-name-input"] : null);
    if (why) {
      setError(why[0]);
      scrollToId(why[1]);
      if (why[1] === "trade-name-input") document.getElementById(why[1])?.focus({ preventScroll: true });
      return;
    }
    setError("");
    setBusy(true);
    const tab = window.open("", "_blank");
    try {
      const [first, ...others] = devices.map(devicePayload);
      const [w1, ...moreWants] = wants.map(wantPayload);
      const body = { ...first, name: name.trim(), wanted_product_id: w1.product_id, wanted_order: w1.order, extra_devices: others, extra_wanted: moreWants.filter((w) => w.product_id || w.order) };
      const { data } = await api.post("/trade-requests", body);
      const href = waLink(message(name, devices, data, diffPay));
      track("contact", { source: "troca" });
      if (tab) tab.location.href = href;
      else window.location.href = href;
      setSent({ code: data?.code, href });
    } catch (err) {
      tab?.close();
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="trade-sent" className="rounded-[28px] border border-emerald-400/40 bg-emerald-400/[0.06] p-6 sm:p-8">
        <p className="font-display text-3xl font-bold text-white">Recebemos sua avaliação!</p>
        <p className="mt-2 text-sm text-zinc-300">Número da avaliação: <strong data-testid="trade-code" className="text-white">{sent.code}</strong>. Seus prints já chegaram para a loja. Se o WhatsApp não abriu, toque abaixo para enviar o resumo.</p>
        <a data-testid="trade-sent-whatsapp" href={sent.href} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-black"><WhatsAppIcon className="h-4 w-4" /> Abrir WhatsApp</a>
      </div>
    );
  }

  return (
    <form data-testid="trade-form" onSubmit={submit} noValidate className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-10">
      <div className="space-y-8 lg:col-span-7">
        {devices.map((d, i) => {
          const setD = updateAt(setDevices, i);
          const sfx = sfxOf(i);
          return (
            <div key={d.uid} id={`trade-device-${i + 1}`} data-testid={`trade-device-${i + 1}`} className="scroll-mt-28 space-y-3">
              {devices.length > 1 && (
                <div className="flex items-center justify-between gap-3 px-1">
                  <p className="font-display text-xl font-bold text-white">Aparelho {i + 1}</p>
                  <button type="button" data-testid={`trade-device-remove-${i + 1}`} onClick={() => removeDevice(i)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-zinc-300 transition-colors hover:bg-white/10 hover:text-white">
                    <Trash2 className="h-4 w-4" /> Remover
                  </button>
                </div>
              )}
              <TradeDevice f={d} setF={setD} sfx={sfx} />
              <TradeCondition f={d} setF={setD} sfx={sfx} bonus={bonus} />
              <Question label="Prints do aparelho" hint="Ajudam a conferir e confirmar todos os detalhes por aqui." testid={`trade-q-prints${sfx}`}>
                <TradePrints sfx={sfx} prints={d.prints} setPrints={(u) => setD((s) => ({ ...s, prints: typeof u === "function" ? u(s.prints) : u }))} />
              </Question>
            </div>
          );
        })}
        {devices.length < MAX_DEVICES && (
          <button type="button" data-testid="trade-device-add" onClick={addDevice}
            className="flex w-full items-center justify-center gap-2 rounded-[22px] border border-dashed border-white/25 py-4 text-sm font-semibold text-white transition-colors hover:border-white/50 hover:bg-white/[0.04]">
            <Plus className="h-4 w-4" /> Adicionar outro aparelho na troca ({devices.length}/{MAX_DEVICES})
          </button>
        )}
      </div>
      <div className="space-y-3 lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
        <TradeEstimate items={items} total={total} allPriced={allPriced} wants={wants} category={category} products={products} wantInfos={wantInfos}
          setWant={(i) => updateAt(setWants, i)} addWant={() => setWants((l) => [...l, newWant()])} removeWant={(i) => setWants((l) => l.filter((_, j) => j !== i))}
          canAddWant={wants.length < MAX_DEVICES && !!wantInfos[wants.length - 1]} wantedTotal={wantedTotal} diff={diff} diffPay={diffPay} setDiffPay={setDiffPay} />
        <input id="trade-name-input" data-testid="trade-name-input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" className={tradeInput} />
        {error && <p data-testid="trade-error" className="text-sm text-amber-300">{error}</p>}
        <button ref={mainButton} data-testid="trade-submit-button" disabled={busy} className="flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] py-4 text-base font-bold text-black transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-70">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <WhatsAppIcon className="h-5 w-5" />} {busy ? "Enviando…" : "Enviar avaliação no WhatsApp"}
        </button>
      </div>
      <TradeStickyBar target={mainButton} est={allPriced ? { value: total } : null} diff={diff} busy={busy} error={error} />
    </form>
  );
};
